import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, TrendingUp, Users, Paperclip, Check } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/api";

const BENEFITS = [
  {
    icon: HandHeart,
    title: "Meaningful Work",
    body: "Make a real difference by helping individuals live safely, confidently, and independently in their communities.",
  },
  {
    icon: TrendingUp,
    title: "Grow With Us",
    body: "Build your skills through training, hands-on experience, mentorship, and opportunities for advancement.",
  },
  {
    icon: Users,
    title: "A Team That Cares",
    body: "Join a supportive workplace where employees are respected, appreciated, and recognized for the work they do.",
  },
];

export default function Careers() {
  // Quick Apply form state
  const [qa, setQa] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    preferred_area: "",
  });
  const [qaResume, setQaResume] = useState(null);
  const [qaSubmitting, setQaSubmitting] = useState(false);
  const [qaSubmitted, setQaSubmitted] = useState(false);

  const submitQuickApply = async (e) => {
    e.preventDefault();
    setQaSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(qa).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      if (qaResume) fd.append("resume", qaResume);
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND}/api/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      setQaSubmitted(true);
      setQa({ first_name: "", last_name: "", email: "", phone: "", position: "", preferred_area: "" });
      setQaResume(null);
      toast.success("Interest submitted — our team will be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQaSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page" className="bg-brand-cream">
      <Toaster richColors position="top-right" />
      {/* 1 — Combined cream section: compact hero + three benefit cards */}
      <section className="relative overflow-hidden bg-brand-cream" data-testid="careers-header">
        <svg className="absolute -top-16 -left-16 w-[380px] h-[380px] pointer-events-none hidden md:block" viewBox="0 0 380 380" aria-hidden="true">
          <defs>
            <linearGradient id="careers-ph-navy" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1e2a78" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#1e2a78" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M -20 200 C 60 60, 220 30, 320 100 C 400 160, 380 300, 260 340 C 140 380, 20 320, -20 240 Z"
            fill="url(#careers-ph-navy)"
          />
        </svg>

        <div className="wc-container relative pt-10 md:pt-12 pb-10 md:pb-14">
          {/* Row 1 — Compact hero */}
          <div className="grid grid-cols-1 lg:grid-cols-[58fr_42fr] gap-8 lg:gap-12 items-center">
            {/* LEFT — copy */}
            <div>
              <div
                className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy"
                data-testid="careers-header-eyebrow"
              >
                <span className="h-px w-8 bg-brand-orange" />
                Careers at WeCare II
              </div>
              <h1
                className="mt-4 font-outfit font-semibold tracking-tight text-brand-navy leading-[1.06] text-[2rem] md:text-[2.5rem] lg:text-[2.75rem]"
                data-testid="careers-header-title"
              >
                Do meaningful work with a team{" "}
                <span
                  className="italic font-medium text-brand-orange relative whitespace-nowrap"
                  style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                >
                  that cares
                  <svg
                    className="absolute -bottom-1.5 left-0 w-full"
                    viewBox="0 0 220 12"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M 4 8 C 60 2, 130 12, 216 4"
                      stroke="#f39325"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span className="text-brand-navy">.</span>
              </h1>
              <p
                className="mt-4 text-slate-600 text-[15px] md:text-base leading-relaxed max-w-md"
                data-testid="careers-header-subtitle"
              >
                Join a compassionate team dedicated to helping individuals with disabilities live safely,
                confidently, and as independently as possible in their homes and communities.
              </p>
            </div>

            {/* RIGHT — subtle blended laptop + application SVG (no photo container) */}
            <div className="relative hidden lg:flex items-center justify-end" data-testid="careers-header-image-wrapper" aria-hidden="true">
              <svg viewBox="0 0 380 260" className="w-full max-w-[420px] h-auto" fill="none">
                <defs>
                  <linearGradient id="careers-mark-blob" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1e2a78" stopOpacity="0.06" />
                    <stop offset="100%" stopColor="#1e2a78" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="careers-mark-screen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.75" />
                  </linearGradient>
                </defs>

                {/* soft backdrop blob */}
                <path
                  d="M 20 140 C 40 40, 220 20, 320 70 C 380 110, 360 220, 240 240 C 140 260, 30 220, 20 140 Z"
                  fill="url(#careers-mark-blob)"
                />

                {/* application/résumé sheet — offset behind the laptop */}
                <g opacity="0.7">
                  <rect x="60" y="52" width="130" height="160" rx="8" fill="#ffffff" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1.25" />
                  <text x="72" y="74" fontFamily="'Outfit', sans-serif" fontSize="8" fontWeight="700" fill="#1e2a78" letterSpacing="1.5">APPLICATION</text>
                  <line x1="72" y1="90" x2="176" y2="90" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                  <line x1="72" y1="106" x2="164" y2="106" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                  <line x1="72" y1="122" x2="176" y2="122" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                  <line x1="72" y1="138" x2="150" y2="138" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                  <line x1="72" y1="154" x2="176" y2="154" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                  <line x1="72" y1="170" x2="140" y2="170" stroke="#1e2a78" strokeOpacity="0.35" strokeWidth="1" />
                </g>

                {/* laptop base */}
                <path
                  d="M 100 220 L 340 220 L 350 232 L 90 232 Z"
                  fill="#1e2a78"
                  fillOpacity="0.9"
                />
                {/* laptop screen */}
                <rect x="128" y="76" width="204" height="140" rx="6" fill="#1e2a78" />
                <rect x="134" y="82" width="192" height="128" rx="3" fill="url(#careers-mark-screen)" />

                {/* form on screen */}
                <g>
                  {/* subtle wc mark top-left of screen */}
                  <text x="144" y="100" fontFamily="'Outfit', sans-serif" fontSize="9" fontWeight="700" fill="#1e2a78" letterSpacing="0.5">wecare</text>
                  <circle cx="188" cy="97" r="2.2" fill="#f39325" />

                  {/* form fields */}
                  <rect x="144" y="112" width="82" height="10" rx="2" fill="#1e2a78" fillOpacity="0.08" />
                  <rect x="232" y="112" width="82" height="10" rx="2" fill="#1e2a78" fillOpacity="0.08" />
                  <rect x="144" y="128" width="170" height="10" rx="2" fill="#1e2a78" fillOpacity="0.08" />
                  <rect x="144" y="144" width="170" height="10" rx="2" fill="#1e2a78" fillOpacity="0.08" />

                  {/* submit button */}
                  <rect x="144" y="172" width="80" height="18" rx="9" fill="#f39325" />
                  <text x="184" y="184" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="8" fontWeight="700" fill="#ffffff" letterSpacing="1">SUBMIT</text>
                </g>

                {/* orange checkmark accent — the "yes" moment */}
                <path
                  d="M 322 60 L 332 70 L 354 46"
                  stroke="#f39325"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />

                {/* small orange dot accents */}
                <circle cx="52" cy="60" r="3.5" fill="#f39325" fillOpacity="0.9" />
                <circle cx="360" cy="200" r="3" fill="#f39325" fillOpacity="0.6" />
              </svg>
            </div>
          </div>

          {/* Row 2 — Three compact horizontal benefit cards (supporting the hero) */}
          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-testid="careers-benefits-section">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-brand-bone/70 rounded-2xl border border-brand-stone/60 p-5 md:p-5 flex gap-4 items-start"
                data-testid={`careers-benefit-${title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-xl bg-brand-navy/5 grid place-items-center text-brand-orange shrink-0">
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="font-outfit text-[17px] font-semibold text-brand-navy tracking-tight leading-snug">
                    {title}
                  </h3>
                  <p className="mt-1 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — Open positions / recruiting CTA */}
      <section
        id="open-positions"
        className="py-16 md:py-20 bg-brand-navy text-white relative overflow-hidden"
        data-testid="careers-cta-section"
      >
        {/* subtle orange glow accent */}
        <svg className="absolute -bottom-24 -left-24 w-[400px] h-[400px] pointer-events-none opacity-60" viewBox="0 0 400 400" aria-hidden="true">
          <defs>
            <linearGradient id="careers-orange" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f39325" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#f39325" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="200" fill="url(#careers-orange)" />
        </svg>

        <div className="wc-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
                <span className="h-px w-8 bg-brand-orange" />
                Join Our Team
              </div>
              <h2 className="mt-5 font-outfit text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
                Ready to make a{" "}
                <span className="italic font-medium text-brand-orange" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  difference
                </span>?
              </h2>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
                We&apos;re looking for compassionate, dependable people who believe everyone deserves the opportunity to
                live a full and independent life.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange" data-testid="careers-areas-line">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
                Opportunities across Hampton Roads &amp; the Eastern Shore
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-orange text-white hover:brightness-95 px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
                  style={{ transition: "filter 200ms ease" }}
                  data-testid="careers-view-positions-btn"
                >
                  Explore Opportunities <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white hover:bg-white/10 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.22em]"
                  style={{ transition: "background-color 200ms ease" }}
                  data-testid="careers-email-btn"
                >
                  Email Our Team <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* RIGHT — compact Quick Apply form (replaces old Opportunities box) */}
            <div className="rounded-[1.5rem] bg-brand-bone text-slate-800 p-6 md:p-7 shadow-[0_18px_40px_rgba(15,26,74,0.18)]" data-testid="careers-quick-apply">
              {qaSubmitted ? (
                <div className="py-8 text-center" data-testid="careers-quick-apply-success">
                  <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center mx-auto">
                    <Check className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="mt-4 font-outfit text-xl font-semibold text-brand-navy">Thank you.</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    Your interest has been received. Our team will contact qualified candidates regarding current opportunities.
                  </p>
                  <button
                    type="button"
                    onClick={() => setQaSubmitted(false)}
                    className="mt-5 text-[11px] font-bold uppercase tracking-widest text-brand-navy hover:text-brand-orange"
                    data-testid="careers-quick-apply-reset-btn"
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form onSubmit={submitQuickApply} className="space-y-4" data-testid="careers-quick-apply-form">
                  <div>
                    <h3 className="font-outfit text-xl font-semibold text-brand-navy tracking-tight">Interested in joining our team?</h3>
                    <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
                      Send us your information and our team will be in touch.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      required
                      type="text"
                      placeholder="First name*"
                      value={qa.first_name}
                      onChange={(e) => setQa({ ...qa, first_name: e.target.value })}
                      data-testid="qa-first-name"
                      className="rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                    />
                    <input
                      required
                      type="text"
                      placeholder="Last name*"
                      value={qa.last_name}
                      onChange={(e) => setQa({ ...qa, last_name: e.target.value })}
                      data-testid="qa-last-name"
                      className="rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      required
                      type="email"
                      placeholder="Email*"
                      value={qa.email}
                      onChange={(e) => setQa({ ...qa, email: e.target.value })}
                      data-testid="qa-email"
                      className="rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                    />
                    <input
                      required
                      type="tel"
                      placeholder="Phone*"
                      value={qa.phone}
                      onChange={(e) => setQa({ ...qa, phone: e.target.value })}
                      data-testid="qa-phone"
                      className="rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                    />
                  </div>

                  <select
                    required
                    value={qa.position}
                    onChange={(e) => setQa({ ...qa, position: e.target.value })}
                    data-testid="qa-position"
                    className="w-full rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                  >
                    <option value="" disabled>Position of interest*</option>
                    <option value="Direct Support Professional">Direct Support Professional</option>
                    <option value="Personal Care Assistant">Personal Care Assistant</option>
                    <option value="Other">Other</option>
                  </select>

                  <select
                    value={qa.preferred_area}
                    onChange={(e) => setQa({ ...qa, preferred_area: e.target.value })}
                    data-testid="qa-area"
                    className="w-full rounded-lg bg-white border border-brand-stone/70 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                  >
                    <option value="">Preferred area</option>
                    <option value="Any">Any / No preference</option>
                    {["Virginia Beach","Chesapeake","Norfolk","Portsmouth","Suffolk","Hampton","Newport News","Eastern Shore"].map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>

                  <label
                    className="flex items-center gap-3 rounded-lg border border-dashed border-brand-stone/70 px-3 py-2.5 text-[13px] text-slate-600 cursor-pointer hover:border-brand-navy/40"
                    style={{ transition: "border-color 200ms ease" }}
                    data-testid="qa-resume-label"
                  >
                    <Paperclip className="w-4 h-4 text-brand-orange shrink-0" />
                    <span className="truncate flex-1">
                      {qaResume ? qaResume.name : "Upload résumé (optional, PDF or DOC · 5MB max)"}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={(e) => setQaResume(e.target.files?.[0] || null)}
                      className="hidden"
                      data-testid="qa-resume-input"
                    />
                  </label>

                  <button
                    type="submit"
                    disabled={qaSubmitting}
                    data-testid="qa-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange text-white hover:brightness-95 disabled:opacity-60 px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
                    style={{ transition: "filter 200ms ease" }}
                  >
                    {qaSubmitting ? "Submitting…" : (<>Submit Interest <ArrowRight className="w-3.5 h-3.5" /></>)}
                  </button>

                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Our team will contact qualified candidates regarding current opportunities.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
